import React, {
  FC,
  KeyboardEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { defineMessages, useIntl } from "react-intl";
import {
  Menu,
  MenuItem,
  Corner,
  MenuController,
} from "@natera/material/lib/menu";
import { DialogsContext } from "@app/pippa/provider";
import { IconButton } from "@natera/material/lib/button";
import { KeyTypes } from "@app/pippa/models";
import PersistentMenuSvg from "@app/pippa/assets/svg/persistent-menu.svg";
import "./header.scss";

const messages = defineMessages({
  widgetName: {
    id: "widgetName",
    defaultMessage: "Natera Results Assistant",
  },
  persistentMenuButtonLabel: {
    id: "persistentMenuButtonLabel",
    defaultMessage: "Open menu",
  },
  closeWidgetMenuItemTitle: {
    id: "closeWidgetMenuItemTitle",
    defaultMessage: "Close the Assistant",
  },
});

const Header: FC = () => {
  const intl = useIntl();
  const { closeWidgetDialog } = useContext(DialogsContext);
  const headerRef = React.useRef<HTMLHeadElement | null>(null);
  const rightHolderRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleMenuClick = () => {
    closeWidgetDialog.open({});
  };

  const handleKeyboardClick = (e: KeyboardEvent<HTMLLIElement>) => {
    const currentKey = e.key.toLowerCase();
    if (currentKey === KeyTypes.ENTER || currentKey === KeyTypes.SPACE) {
      handleMenuClick();
    }
  };

  const menu = (
    <MenuItem
      data-testid="menu-item"
      className="pippa-menu-item"
      onClick={handleMenuClick}
      onKeyDown={(e) => handleKeyboardClick(e)}
    >
      {intl.formatMessage(messages.closeWidgetMenuItemTitle)}
    </MenuItem>
  );

  useEffect(() => {
    const node = rightHolderRef.current;
    if (!node) return;

    const handleRightHolderClick = () => {
      if (isMenuOpen) headerRef.current?.click();
    };

    node.addEventListener("click", handleRightHolderClick);
    return () => node.removeEventListener("click", handleRightHolderClick);
  }, [isMenuOpen]);

  return (
    <header ref={headerRef} className="pippa-widget-header">
      <div className="pippa-widget-header-wrapper">
        <div className="pippa-widget-header__left-holder"></div>
        <div className="pippa-widget-header__bot-info-holder">
          <div className="pippa-widget-header__text-container">
            <h1 className="pippa-widget-header__title">
              {intl.formatMessage(messages.widgetName)}
            </h1>
          </div>
        </div>
        <div
          ref={rightHolderRef}
          data-testid="right-holder"
          className="pippa-widget-header__right-holder"
        >
          <Menu
            menu={menu}
            floating={false}
            corner={Corner.BOTTOM_START}
            onOpen={() => setIsMenuOpen(true)}
            onClose={() => setIsMenuOpen(false)}
            onPointerEnter={undefined}
            onPointerLeave={undefined}
          >
            {(controller: MenuController) => (
              <IconButton
                data-testid="menu-button"
                className="pippa-widget-header__menu-button"
                onClick={controller.toggleMenu}
                aria-label={intl.formatMessage(
                  messages.persistentMenuButtonLabel
                )}
              >
                {PersistentMenuSvg}
              </IconButton>
            )}
          </Menu>
        </div>
        <div className="pippa-widget-header__fix-holder"></div>
      </div>
    </header>
  );
};

export default Header;
